<script lang="ts">
  import { get } from "lodash";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import LimitedOrderingProductRequestModal from "./LimitedOrderingProductRequestModal.svelte";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import type { QuickOrderListIndex } from "@shared/types/QuickOrderList";
  import type { Errors } from "@shared/types/Errors";
  import {
    trackListAdd,
    trackListCreate,
    trackListRemove,
  } from "@shared/services/event_tracking/list_tracking";

  import productContainerDetails from "@shared/filters/product_container_details.js";
  import QuickOrderListStore from "@retailer/svelte/stores/QuickOrderListStore";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import { ListEntity } from "@shared/services/event_tracking/entities";

  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import { onDestroy } from "svelte";

  export let product: Product;
  export let productLine: ProductLine;

  const { trackClick } = EventTracking;

  let open: boolean = false;
  let showNewListForm: boolean = false;
  let formErrors: Errors = { messages: [] };
  let listsForProduct: { [key: number]: boolean } = {};
  let showLimitedOrderingProductRequestModal: boolean = false;

  let quickOrderLists: QuickOrderListIndex[] = null;

  function getProductListInfo() {
    Http(
      {
        method: "GET",
        url: "/api/retailer/quick_order_lists/lists_for_product",
        data: {
          product_id: product.id,
        },
      },
      function (res) {
        res.forEach(function (d) {
          listsForProduct[d.id] = true;
        });
      },
      function error(res) {
        console.error(res);
      },
    );
  }

  async function openModal() {
    if (restrictRepAccess()) {
      return;
    }
    if ($CurrentUserStore.group_director) {
      showLimitedOrderingProductRequestModal = true;
      return;
    }
    open = true;
    quickOrderLists = null;
    Http(
      {
        url: "api/retailer/quick_order_lists",
        method: "GET",
      },
      res => {
        quickOrderLists = res;
        getProductListInfo();
      },
      err => {
        formErrors = { messages: [err] };
      },
    );
  }

  function closeModal() {
    open = false;
  }

  async function addToList(listId: number, cb) {
    Http(
      {
        method: "POST",
        url: "/api/retailer/quick_order_lists/" + listId + "/products",
        data: {
          quick_order_list_product: {
            product_id: product.id,
            inventory_id: product.inventory_id,
          },
        },
      },
      function (res) {
        trackListAdd(
          listId,
          product.id,
          product.product_line_id,
          product.inventory_id,
          product.distributor_id,
        );
        input.newListName = null;
        if (cb) {
          cb();
        }
      },
      function (res) {
        var msg = "There was an error adding the product to a list.\n";
        if ((res || {} || [])["join"]) {
          msg += (res || {} || []).join("\n");
        }
        if (msg.includes("Product is already present")) {
          formErrors = { messages: [msg] };
        }
      },
    );
  }

  async function removeFromList(listId) {
    Http(
      {
        method: "DELETE",
        url: "/api/retailer/quick_order_lists/" + listId + "/products/destroy_by_product_id",
        data: {
          quick_order_list_product: {
            product_id: product.id,
            inventory_id: product.inventory_id,
          },
        },
      },
      function (res) {
        trackListRemove(
          listId,
          product.id,
          product.product_line_id,
          product.inventory_id,
          product.distributor_id,
        );
        listsForProduct[listId] = false;
      },
      function (err) {
        formErrors = { messages: [err] };
      },
    );
  }

  let newQuickOrderListStore;
  let unsubscribe = () => {};
  let unsubscribeErrors = () => {};
  const prepareStore = function () {
    newQuickOrderListStore = QuickOrderListStore({}, null, null);
    unsubscribe = newQuickOrderListStore.subscribe((q: QuickOrderListIndex) => {
      if (!q || !q.id) {
        return;
      }
      trackListCreate(q.id);
      addToList(q.id, () => {
        openModal();
      });
    });
    unsubscribeErrors = newQuickOrderListStore.errors.subscribe(n => {});
  };
  prepareStore();

  onDestroy(() => {
    unsubscribe();
    unsubscribeErrors();
  });

  async function createList() {
    if (!input.newListName || !input.newListName.length) return;
    prepareStore();
    await newQuickOrderListStore.save({ name: input.newListName });
  }

  function changeList(listId: number) {
    // backwards because this is a checkbox change handler
    if (listsForProduct[listId]) {
      addToList(listId, () => {});
    } else {
      removeFromList(listId);
    }
  }

  let input: { newListName: string } = {
    newListName: null,
  };

  function onClick() {
    trackClick(null, new ListEntity({ linkText: "Add to List" }));
    openModal();
  }
</script>

<a
  class="manage-lists-link cursor-pointer accent-body--bold"
  href={"#"}
  on:click|preventDefault={onClick}
>
  <span>Save to List</span>
</a>

<Modal bind:show={open}>
  <FormErrors errors={formErrors} />
  <div class="add-to-list-button__modal">
    <header class="modal__header padding--smaller align-left">
      <div class="default-body--bold">Save to List</div>
      <div class="grid-row">
        <div class="grid-col c5-10 align-left vertical-align-middle">
          <div class="default-small--regular">
            {productContainerDetails()(product, null, null, true)}
          </div>
        </div>
        <div class="grid-col c5-10 align-right vertical-align-middle">
          <a
            class="brand-body--bold cursor-pointer"
            href={"#"}
            on:click|preventDefault={() => (showNewListForm = !showNewListForm)}
          >
            Create New List
            <Arrow direction={showNewListForm ? "up" : "down"} />
          </a>
        </div>
      </div>
      <div class="grid-row">
        <div class="grid-col c10-10 align-left vertical-align-middle">
          {#if showNewListForm}
            <form
              class="media margin-top--smaller ng-animation slide-in-out"
              on:submit|preventDefault={createList}
            >
              <div class="input-wrapper position-relative media-fluid margin-right--smaller">
                <input
                  class="input add-to-list-button__new-input"
                  type="text"
                  placeholder="New List Name"
                  bind:value={input.newListName}
                />
                <input type="submit" class="display-none" />
              </div>
              <div class="media-fixed align-right">
                <button
                  class="button button--secondary"
                  disabled={!get(input, "newListName.length")}>Add New List</button
                >
              </div>
            </form>
          {/if}
        </div>
      </div>
    </header>

    {#if !quickOrderLists}
      <div class="padding--small">Loading...</div>
    {/if}

    <div class="modal__scroll-container padding-top--small padding-bottom--small">
      <div class="grid-container">
        <div class="grid-row">
          <div class="grid-col c12-12">
            {#if quickOrderLists}
              <ul class="list">
                {#each quickOrderLists as list}
                  {#if !list.auto_generated}
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label>
                      <li
                        class="list-item cursor-pointer margin-bottom--smaller add-to-list-button__selectable cursor-pointer"
                        quick-order-list-id={list.id}
                      >
                        <input
                          type="checkbox"
                          bind:checked={listsForProduct[list.id]}
                          on:change={() => changeList(list.id)}
                        />
                        {list.name}
                      </li>
                    </label>
                  {/if}
                {/each}
              </ul>
            {/if}
          </div>
        </div>
      </div>

      <footer class="modal__footer align-right padding--smaller">
        <div class="grid-row">
          <div class="grid-col c12-10 align-right">
            <button class="button button--small button--primary" on:click={closeModal}>Done</button>
          </div>
        </div>
      </footer>
    </div>
  </div>
</Modal>

<LimitedOrderingProductRequestModal
  bind:open={showLimitedOrderingProductRequestModal}
  {product}
  {productLine}
/>
